import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';

import UserSearchInput from './Filters/UserSearchInput';
import ProviderDropdown from './Filters/ProviderDropdown';
import StatusDropdown from './Filters/StatusDropdown';
import UserDropdown from './Filters/UserDropdown';
import { useFilterState } from '../../hooks/useFilterState';

import { DataGridPremium } from '@mui/x-data-grid-premium';
import { adminEmailJobColumns } from './adminEmailJobColumns';
import {
	tableComponentStyles,
	dataGridStyles,
} from './adminEmailJobTableStyles';

const AdminEmailJobTable = ({
	jobs,
	parameters,
	jobCount,
	handlePaginationModelChange,
	handleSortModelChange,
	users,
}) => {
	const navigate = useNavigate();
	const [columns] = useState(adminEmailJobColumns(navigate));
	const searchTimeoutRef = useRef(null);

	// Filter state management with URL synchronization
	const { filters, handleFilterChange } = useFilterState({
		searchText: 'user_search',
		provider: 'provider',
		status: 'status',
		userId: 'user_id',
	});

	// Local state for search input (for debouncing)
	const [searchText, setSearchText] = useState(filters.searchText);

	// Add cleanup in useEffect
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	// Handle search with debouncing
	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchText(value);

		// Clear previous timeout
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		// Debounce URL update - only update after user stops typing
		searchTimeoutRef.current = setTimeout(() => {
			const updatedValue = value.trim();
			handleFilterChange('user_search', updatedValue);
		}, 500);
	};

	return (
		<Box sx={tableComponentStyles.mainContainer}>
			<Box sx={tableComponentStyles.headerContainer}>
				<Typography variant='h6'>Email Ingestion Jobs</Typography>

				<Box sx={tableComponentStyles.filtersContainer}>
					<UserSearchInput value={searchText} onChange={handleSearchChange} />

					<ProviderDropdown
						value={filters.provider}
						onChange={(e) => handleFilterChange('provider', e.target.value)}
					/>

					<StatusDropdown
						value={filters.status}
						onChange={(e) => handleFilterChange('status', e.target.value)}
					/>

					<UserDropdown
						value={filters.userId}
						onChange={(e) => handleFilterChange('user_id', e.target.value)}
						users={users}
					/>
				</Box>
			</Box>

			<Box sx={tableComponentStyles.dataGridContainer}>
				<DataGridPremium
					data-testid='admin-email-datagrid'
					sx={dataGridStyles}
					rows={jobs}
					columns={columns}
					paginationMode='server'
					paginationModel={{
						page: Number(parameters.page),
						pageSize: Number(parameters.per),
					}}
					pagination
					onPaginationModelChange={handlePaginationModelChange}
					rowCount={jobCount}
					sortingMode='server'
					sortModel={
						parameters.sort_column
							? [
									{
										field: parameters.sort_column,
										sort: parameters.sort_order,
									},
								]
							: []
					}
					onSortModelChange={handleSortModelChange}
					pageSizeOptions={[5, 10, 25]}
					disableRowSelectionOnClick
					autoHeight
				/>
			</Box>
		</Box>
	);
};

AdminEmailJobTable.propTypes = {
	jobs: PropTypes.array.isRequired,
	parameters: PropTypes.object.isRequired,
	jobCount: PropTypes.number.isRequired,
	handlePaginationModelChange: PropTypes.func.isRequired,
	handleSortModelChange: PropTypes.func.isRequired,
	users: PropTypes.array.isRequired,
};

export default AdminEmailJobTable;
